import React, { useEffect, useState } from 'react';
import { convertToCurrency } from '../../helpers/converters/converters';
import ListItems from '../listItems/ListItems';
import { NON_LEASABLE_MULTIPLIER, NON_LEASABLE_PERCENTAGE } from '../../constants/cart-validation';
import { SegmentProps } from '../../state-machine/actions/segment';
import { Application, CartValidationTypes, Dealer, Details } from '../../gql/graphql';
import { getNonLeasableItems } from '../../helpers/converters/getNonLeasableItems';
import { getTotalCartAmount } from '../../helpers/converters/getTotalCartAmount';
import { useTranslation } from 'react-i18next';

type IProps = SegmentProps & {
  cartValidationErr: CartValidationTypes;
  approvalAmount: string;
  minimumSpend: number | null;
  application?: Application;
  dealer: Dealer;
  orderDetails: Details;
};

const CartValidationErrContent: React.FC<IProps> = ({ application, dealer, orderDetails, cartValidationErr, approvalAmount, minimumSpend, track }) => {
  const { t } = useTranslation();
  const [cartDoesNotMeetMin, setCartDoesNotMeetMin] = useState(false);
  const [cartGreaterThanApproval, setCartGreaterThanApproval] = useState(false);
  const [tooManyNonLeaseableItems, setTooManyNonLeaseableItems] = useState(false);
  const nonLeasableItems = getNonLeasableItems(orderDetails);
  const totalCartAmount = getTotalCartAmount(orderDetails);

  useEffect(() => {
    switch (cartValidationErr) {
      case 'CartTotalExceedsApproval':
        track('Application Approved with cart total greater than approval amount', {
          approvalAmount: approvalAmount,
          cartTotal: totalCartAmount,
          minimumSpend: minimumSpend,
        });
        setCartGreaterThanApproval(true);
        break;
      case 'CartTotalDoesNotMeetMinimum':
        track(`Application Approved with cart total less than required minimum (${convertToCurrency(minimumSpend ?? 0)}`, {
          approvalAmount: approvalAmount,
          cartTotal: totalCartAmount,
          minimumSpend: minimumSpend,
        });
        setCartDoesNotMeetMin(true);
        break;
      case 'NonLeaseableItemsTotal':
        track('Application Approved with too many nonLeaseable Items in Cart', {
          approvalAmount: approvalAmount,
          cartTotal: totalCartAmount,
          minimumSpend: minimumSpend,
        });
        setTooManyNonLeaseableItems(true);
        break;
      default:
        break;
    }
  }, [minimumSpend, approvalAmount, totalCartAmount, cartValidationErr]); // eslint-disable-line react-hooks/exhaustive-deps

  const CartTooHighComponent = () => {
    return (
      <h6>
        {t(($) => $.componentCartValidationErrorContent.totalAboveMaximum, {
          cartTotal: convertToCurrency(totalCartAmount),
          approvalAmount: convertToCurrency(parseInt(approvalAmount), 0),
        })}
      </h6>
    );
  };

  const CartDoesNotMeetMinComponent = () => {
    return (
      <h6>
        {t(($) => $.componentCartValidationErrorContent.totalBelowMinimum, {
          cartTotal: convertToCurrency(totalCartAmount),
          minimumSpend: convertToCurrency(minimumSpend ?? 0),
        })}
      </h6>
    );
  };

  const CartContainsTooManyNonLeasableComponent = () => {
    return (
      <div>
        <h6 style={{ marginBottom: '0.25rem' }}>
          {t(($) => $.componentCartValidationErrorContent.tooManyNonLeasable, {
            nonLeasablePercentage: NON_LEASABLE_PERCENTAGE,
            nonLeasableAmount: convertToCurrency(totalCartAmount * NON_LEASABLE_MULTIPLIER),
            cartTotal: convertToCurrency(totalCartAmount),
          })}
        </h6>
        <ListItems items={nonLeasableItems} />
      </div>
    );
  };
  return (
    <div style={{ marginTop: '2rem' }}>
      {cartGreaterThanApproval && <CartTooHighComponent />}

      {cartDoesNotMeetMin && <CartDoesNotMeetMinComponent />}

      {tooManyNonLeaseableItems && <CartContainsTooManyNonLeasableComponent />}
    </div>
  );
};

export default CartValidationErrContent;
